import { dictionaries, type Locale, type TranslationKey } from './dictionaries';

const SUPPORTED: readonly Locale[] = ['es', 'de', 'pt', 'it'];
const FALLBACK: Locale = 'es';
const STORAGE_KEY = 'compras-locale';

class I18nService {
  private locale: Locale = FALLBACK;
  private listeners = new Set<(locale: Locale) => void>();

  constructor() {
    // Prioridad: localStorage > navegador > fallback
    const saved = this.getSavedLocale();
    if (saved) {
      this.locale = saved;
    } else {
      this.locale = this.detectFromBrowser();
    }
    document.documentElement.lang = this.locale;
  }

  getLocale(): Locale {
    return this.locale;
  }

  getSupportedLocales(): readonly Locale[] {
    return SUPPORTED;
  }

  setLocale(locale: Locale): void {
    if (!SUPPORTED.includes(locale)) {
      throw new Error(`Unsupported locale: ${locale}`);
    }
    this.locale = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Ignorar si localStorage no está disponible
    }
    document.documentElement.lang = locale;
    this.listeners.forEach(fn => fn(locale));
  }

  onChange(fn: (locale: Locale) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Traduce con interpolación SEGURA de parámetros.
   * Las variables se reemplazan como texto plano, sin evaluación.
   * Ej: t('orders.weekOf', { week: 18, year: 2026 }) → "Semana 18 de 2026"
   */
  t(key: TranslationKey, params?: Record<string, string | number>): string {
    const dict = dictionaries[this.locale] ?? dictionaries[FALLBACK];
    const template = (dict as Record<string, string>)[key]
                  ?? (dictionaries[FALLBACK] as Record<string, string>)[key]
                  ?? key;

    if (!params) return template;

    return template.replace(/\{(\w+)\}/g, (match, name) => {
      const value = params[name];
      return value !== undefined ? String(value) : match;
    });
  }

  formatDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(this.locale, {
      year: 'numeric', month: 'short', day: 'numeric',
    }).format(d);
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.locale, options).format(value);
  }

  private getSavedLocale(): Locale | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED.includes(saved as Locale)) {
        return saved as Locale;
      }
    } catch {
      // localStorage puede fallar en modo incógnito
    }
    return null;
  }

  private detectFromBrowser(): Locale {
    const browserLang = navigator.language.slice(0, 2).toLowerCase();
    return SUPPORTED.includes(browserLang as Locale) ? (browserLang as Locale) : FALLBACK;
  }
}

export const i18n = new I18nService();
